import { uniqueId, mapObject, constant } from 'underscore';
import classnames from 'classnames';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import { FocusLock } from '@bingads-webui-react/focus-lock';
import { Button, Glyphicon } from '@bingads-webui-react/primitive';
import { isNumeric, windowIsUndefined } from './helper';
import { getPanelStyles } from './styles';

const currentPanel = {};

class Panel extends React.PureComponent {
  constructor(props) {
    super(props);
    this.container = null;
    this.parent = null;
    this.panelId = uniqueId('panel_id_');
    currentPanel[this.panelId] = props.isOpen;
  }

  componentWillUnmount() {
    delete currentPanel[this.panelId];
    if (this.container && this.props.getContainer) {
      this.container.parentNode.removeChild(this.container);
    }
  }

  getParent = ({ getContainer }) => {
    /* istanbul ignore if */
    if (windowIsUndefined) {
      return;
    }

    if (typeof getContainer === 'function') {
      this.parent = getContainer();
    } else if (typeof getContainer === 'object' && getContainer instanceof window.HTMLElement) {
      this.parent = getContainer;
    } else {
      // by default getContainer is 'body', a string selector
      const dom = document.querySelector(getContainer);
      this.parent = dom;
    }
  };

  initContainer = (props) => {
    this.getParent(props);
    if (props.getContainer && this.parent) {
      this.container = this.createPanelContainer();
    }
  };

  createPanelContainer = () => {
    /* istanbul ignore if */
    if (windowIsUndefined) {
      return null;
    }

    const container = document.createElement('div');

    this.parent.appendChild(container);
    container.className = this.props.classes.panelContainer;
    return container;
  };

  // disabled ruels: jsx-a11y/no-static-element-interactions jsx-a11y/click-events-have-key-events
  // eslint-disable-next-line
  renderMask = () => (<div
    className="panel-mask"
  />);

  renderCloseIcon = () => (
    <div className="panel-close-container">
      <Button onClick={this.props.onClose} aria-label={this.props.i18n.getString(_TL_('Close'))} className="panel-close">
        <Glyphicon
          className="panel-cancel-icon"
          glyph="ba-Cancel"
        />
      </Button>
    </div>
  );

  renderHeader() {
    const { title } = this.props;

    // return early to in case empty panel-header show out
    if (!title) {
      return null;
    }

    return (
      <div className="panel-header">
        <h2 className="panel-title" id="panel-title">{title}</h2>
      </div>
    );
  }

  renderDescription() {
    const { description } = this.props;

    return (
      <React.Fragment>
        {description ? <div className="panel-description">{description}</div> : null}
      </React.Fragment>
    );
  }

  renderPanel() {
    const {
      direction,
      children,
      width,
      height,
      isOpen,
      showCloseIcon,
      panelReadyAriaLabel,
      i18n,
    } = this.props;
    const isHorizontal = direction === 'left' || direction === 'right';
    const translation = `translate${isHorizontal ? 'X' : 'Y'}`;
    const translationPos = direction === 'left' || direction === 'top' ? '-100%' : '100%';
    const transform = isOpen ? '' : `${translation}(${translationPos})`;

    return (
      <FocusLock
        onExit={this.props.onClose}
        lockRegionAriaLabel={panelReadyAriaLabel || i18n.getString(_TL_('Side panel is ready'))}
        focusFirstItemOnMount
      >
        <div
          className="panel-content"
          style={{
            transform,
            msTransform: transform,
            width: isNumeric(width) ? `${width}px` : width,
            height: isNumeric(height) ? `${height}px` : height,
          }}
          role="dialog"
          aria-labelledby="panel-title"
        >
          {showCloseIcon && this.renderCloseIcon()}
          {this.renderHeader()}
          {this.renderDescription()}
          <div className="panel-body" >
            {children}
          </div>
        </div>
      </FocusLock>
    );
  }

  render() {
    const {
      direction,
      showMask,
      isOpen,
    } = this.props;
    currentPanel[this.panelId] = isOpen && this.container;
    const panelClassname = classnames('panel', this.props.panelClassName, {
      [`panel-${direction}`]: true,
      isOpen,
    });

    if (isOpen && !this.container) {
      this.initContainer(this.props);
    }

    // return early to in case no needed rendering
    if (!this.container || !isOpen) {
      return null;
    }

    // Update classname when rerender, otherwise style could broke if jss hash name changed by parent
    this.container.className = this.props.classes.panelContainer;

    const panel = (
      <div className={panelClassname} >
        {showMask && this.renderMask()}
        {this.renderPanel()}
      </div>);

    return ReactDOM.createPortal(panel, this.container);
  }
}

Panel.propTypes = {
  /**
   * Define the classes for Panel container, Panel container name can be customized.
   * Default value is 'panelContainer'
   */
  classes: PropTypes.shape(mapObject(
    getPanelStyles({}, {}),
    constant(PropTypes.string.isRequired)
  )).isRequired,
  /**
   * Custom class name for panel.
   */
  panelClassName: PropTypes.string,
  /**
   * Return the mounted node for Panel.
   * Default value is 'body'
   * Type: Selectors | HTMLElement | () => HTMLElement
   */
  getContainer: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.func,
  ]),
  /**
   * Whether to show mask or not
   * Default value is true
   */
  showMask: PropTypes.bool,
  /**
   * Whether the Panel dialog is open or not.
   * Default value is false
   */
  isOpen: PropTypes.bool,
  /**
   * The direction of the Panel.
   * 'left', 'right', 'top', 'bottom'
   * Default value is 'right'
   */
  direction: PropTypes.oneOf(['left', 'right', 'top', 'bottom']),
  /**
   * Width of the Panel
   * Default value is null (100%)
   */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Height of the Panel
   * Default value is null (100%)
   */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * The title for Panel
   * Default value is null
   */
  title: PropTypes.node,
  /**
   * The description for Panel
   * Default value is null
   */
  description: PropTypes.node,
  /**
   * The children for Panel, also the content for the panel
   */
  children: PropTypes.node.isRequired,
  /**
   * Whether the Panel show close icon
   * Default value is true
   */
  showCloseIcon: PropTypes.bool,
  /**
   * Specify a callback that will be called when a user clicks mask, close button
   * onClose is required to close the panel
   */
  onClose: PropTypes.func.isRequired,
  /**
   * panel ready aria label text
   */
  panelReadyAriaLabel: PropTypes.string,
  /**
   * i18n for localization
   */
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
};

Panel.defaultProps = {
  getContainer: 'body',
  isOpen: false,
  showMask: true,
  direction: 'right',
  width: null,
  height: null,
  title: null,
  description: null,
  panelClassName: null,
  showCloseIcon: true,
  panelReadyAriaLabel: '',
};

export const StyledPanel = withDefaultStyles(Panel, getPanelStyles);
